import { ALL_FIELD_TYPES } from '@carto/kepler.gl/constants'
import { AggregationType, AggregationTypes, CategoryOrderBy } from '@carto/api-client'
import { CategoryWidget, MapDatasetEnriched } from 'features/builder'
import { KeplerFieldType } from 'features/builder/types'
import { DefaultOperationEntries, isCustomAggregationExpSupported } from './utils'
import { MapType } from 'features/maps/types'

const availableFieldTypesForColumn: KeplerFieldType[] = [ALL_FIELD_TYPES.string, ALL_FIELD_TYPES.boolean]
const availableFieldTypesForColumnRaster: KeplerFieldType[] = [ALL_FIELD_TYPES.integer]
const availableFieldTypesForOperationColumn: KeplerFieldType[] = [ALL_FIELD_TYPES.integer, ALL_FIELD_TYPES.real]

export function getAvailableFieldTypesForColumn(mapType: MapType): KeplerFieldType[] {
  return mapType === MapType.raster ? availableFieldTypesForColumnRaster : availableFieldTypesForColumn
}

export function getAvailableFieldTypesForOperationColumn(): KeplerFieldType[] {
  return availableFieldTypesForOperationColumn
}

export const DEFAULT_ORDER_BY: CategoryOrderBy = 'frequency_desc'

export function isCategoryWidgetValid(widget: CategoryWidget, { source }: { source: MapDatasetEnriched }): boolean {
  const columnType = source.fields.find((f) => f.id === widget.column)?.type
  const columnValid =
    Boolean(columnType) && getAvailableFieldTypesForColumn(source.type).includes(columnType as KeplerFieldType)

  if (widget.operation === AggregationTypes.Custom) {
    return columnValid && isCustomAggregationExpSupported({ source }) && Boolean(widget.operationExp)
  }

  if (widget.operation === AggregationTypes.Count) {
    return columnValid
  }

  const operationColumnType = source.fields?.find((f) => f.id === widget.operationColumn)?.type as KeplerFieldType
  const operationColumnValid =
    operationColumnType && getAvailableFieldTypesForOperationColumn().includes(operationColumnType)

  return Boolean(columnValid && operationColumnValid)
}

export function validateCategoryWidget(
  widget: CategoryWidget,
  { source }: { source: MapDatasetEnriched }
): CategoryWidget {
  const columnType = source.fields.find((f) => f.id === widget.column)?.type
  const columnValid = columnType && getAvailableFieldTypesForColumn(source.type).includes(columnType)

  if (!columnValid) {
    widget.column = ''
  }

  const availableOperations = categoryWidgetAvailableOperations(source)
  if (!widget.operation || !availableOperations.includes(widget.operation)) {
    widget.operation = AggregationTypes.Count
  }

  if (widget.operation === AggregationTypes.Count) {
    widget.operationColumn = ''
    widget.isValid = Boolean(columnValid)
  } else if (widget.operation === AggregationTypes.Custom) {
    if (!isCustomAggregationExpSupported({ source })) {
      widget.operation = AggregationTypes.Count
      widget.isValid = Boolean(columnValid)
    } else {
      widget.operationColumn = ''
      widget.isValid = Boolean(columnValid && widget.operationExp)
    }
  } else {
    const operationColumnType = source.fields.find((f) => f.id === widget.operationColumn)?.type
    const operationColumnValid =
      operationColumnType && getAvailableFieldTypesForOperationColumn().includes(operationColumnType)
    if (!operationColumnValid) {
      widget.operationColumn = ''
    }
    widget.isValid = Boolean(columnValid && operationColumnValid)
  }

  if (!widget.orderBy) {
    widget.orderBy = DEFAULT_ORDER_BY
  }

  return widget
}

export function categoryWidgetAvailableOperations(source: MapDatasetEnriched): AggregationType[] {
  const supportsCustomAggregation = isCustomAggregationExpSupported({ source })
  const supportsNormalAggregation = source.fields.some((f) =>
    getAvailableFieldTypesForOperationColumn().includes(f.type)
  )

  const result: AggregationType[] = []
  if (!supportsNormalAggregation) {
    result.push(AggregationTypes.Count)
  } else {
    result.push(...DefaultOperationEntries)
  }
  if (supportsCustomAggregation) {
    result.push(AggregationTypes.Custom)
  }
  return result
}

export function isWidgetCategoryAvailable(source: MapDatasetEnriched): boolean {
  return source.fields.some((f) => getAvailableFieldTypesForColumn(source.type).includes(f.type))
}
